import axios from 'axios';
import type { AxiosError, AxiosResponse } from 'axios';
import { setupCache } from 'axios-cache-adapter';
import localforage from 'localforage';
import { HttpError } from 'react-admin';

import { API_URL, TENANT_CODE } from '../env';
import { refreshToken } from '~/api/auth';
import { getStoreRef } from '~/utils/storeRef';
import { approvalRequired } from '~/ducks/approvalRequired';
import { notification } from '~/ducks/snackbarNotifications';

const DEFAULT_HTTP_HEADERS: any = {
  Accept: 'application/json',
};
if (TENANT_CODE) {
  DEFAULT_HTTP_HEADERS['V-Tenant'] = TENANT_CODE;
}

// Shared refresh handling (prevents multiple concurrent refresh calls)
let refreshRequest: Promise<{ accessToken: string; refreshToken: string }> | null = null;

async function getRefreshedAccessToken(): Promise<string> {
  const oldRefreshToken = localStorage.getItem('refreshToken');
  if (!oldRefreshToken) throw new Error('No refresh token available');

  if (!refreshRequest) {
    refreshRequest = refreshToken(oldRefreshToken)
      .then(({ data }) => {
        const { accessToken, refreshToken: newRefreshToken } = data;
        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        return data;
      })
      .finally(() => {
        refreshRequest = null;
      });
  }

  const { accessToken } = await refreshRequest;
  return accessToken;
}

function notifyMetadataWarning(response: AxiosResponse) {
  const warning = response.data?.__metadata?.warning;
  if (warning) {
    getStoreRef()?.dispatch(notification({ severity: 'warning', message: warning }));
  }
}

function forceLogout() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('role');
  localStorage.removeItem('identity');
  localStorage.removeItem('justLoggedIn');
  const tenantParam = TENANT_CODE ? `&tenant=${TENANT_CODE}` : '';
  window.location.href = `${API_URL}/auth/login?referrer_uri=${window.location.origin}/auth/redirect${tenantParam}`;
}

async function refreshAndRetry(error: AxiosError, client: any) {
  if (error.response) {
    const originalRequest: any = error.config;

    if (
      (error.response.status === 403 || error.response.status === 401) &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refreshToken')
    ) {
      originalRequest._retry = true;
      try {
        const newAccessToken = await getRefreshedAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['V-Access-Token'] = newAccessToken;
        return client(originalRequest);
      } catch {
        forceLogout();
      }
    }

    return Promise.reject(
      new HttpError(
        error.response.statusText || 'Unknown error',
        error.response.status,
        error.response.data
      )
    );
  } else {
    return Promise.reject(new HttpError('Connection failed', 0, null));
  }
}

const instance = axios.create({
  baseURL: API_URL,
  headers: DEFAULT_HTTP_HEADERS,
});

instance.interceptors.request.use(
  (config) => {
    config.headers['V-Access-Token'] = localStorage.getItem('token');
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    if (response.status === 202 && response.data?.approvalCaseId) {
      getStoreRef()?.dispatch(approvalRequired(response.data.approvalCaseId));
    }
    notifyMetadataWarning(response);
    return response;
  },
  async (error: AxiosError) => {
    return refreshAndRetry(error, instance);
  }
);

export default instance;

// Create `axios-cache-adapter` instance
const store = localforage.createInstance({
  driver: localforage.LOCALSTORAGE,
  name: 'axios',
});

const cache = setupCache({
  maxAge: 15 * 60 * 1000,
  debug: true,
  store,
});
//
const instanceWithCache = axios.create({
  adapter: cache.adapter,
  baseURL: API_URL,
  headers: DEFAULT_HTTP_HEADERS,
});

instanceWithCache.interceptors.request.use(
  (config) => {
    config.headers['V-Access-Token'] = localStorage.getItem('token');
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instanceWithCache.interceptors.response.use(
  function (response) {
    notifyMetadataWarning(response);
    return response;
  },
  async (error: AxiosError) => {
    return refreshAndRetry(error, instanceWithCache);
  }
);

export const axiosWithCache = instanceWithCache;

export type ResponseType<R = any> = Promise<AxiosResponse<R>>;
